import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { EmployeeHubView } from '../components/employeeHub/EmployeeHubView';
import { parseApiError } from '../utils/apiError';
import { canWith } from '../utils/rbacPermissions';
import { isManagerPersona, isRrhhOperatorPersona } from '../utils/rrhhPersona';
import { getHubSummary } from '../services/communityService';

function empStatusVariant(s) {
  return ['active', 'onboarding', 'inactive', 'offboarding'].includes(s) ? s : 'active';
}

const STATE = {
  LOADING: 'loading',
  OK: 'ok',
  UNAUTH: 'unauth',
  LINK_SUSPENDED: 'link_suspended',
  FORBIDDEN: 'forbidden',
  NOT_LINKED: 'not_linked',
  NOT_FOUND: 'not_found',
  SERVER: 'server',
  NETWORK: 'network',
  UNKNOWN: 'unknown',
};

function classifyError(err) {
  const { status, code } = parseApiError(err);
  if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') return STATE.NETWORK;
  if (!err?.response && !status) return STATE.NETWORK;
  if (status === 401) return STATE.UNAUTH;
  if (status === 403 && code === 'identity_link_suspended') return STATE.LINK_SUSPENDED;
  if (status === 403) return STATE.FORBIDDEN;
  if (status === 409 && code === 'employee_not_linked') return STATE.NOT_LINKED;
  if (status === 404) return STATE.NOT_FOUND;
  if (status >= 500) return STATE.SERVER;
  return STATE.UNKNOWN;
}

export function EmployeePortal() {
  const { t, authMe, permSummary, isProductionUi } = useApp();
  const [data, setData] = useState(null);
  const [state, setState] = useState(STATE.LOADING);
  const [supportId, setSupportId] = useState(null);
  const [managerStats, setManagerStats] = useState(null);
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerError, setManagerError] = useState(false);
  const [commsPreview, setCommsPreview] = useState([]);
  const [commsLoading, setCommsLoading] = useState(false);
  const [commsError, setCommsError] = useState(false);
  const [hubCulture, setHubCulture] = useState(null);
  const [hubCultureLoading, setHubCultureLoading] = useState(false);
  const [hubCultureError, setHubCultureError] = useState(false);

  const showManagerBlock = isManagerPersona(permSummary, isProductionUi);

  const persona = useMemo(() => {
    if (isRrhhOperatorPersona(permSummary, authMe, isProductionUi)) return 'rrhh';
    if (showManagerBlock) return 'manager';
    return 'employee';
  }, [permSummary, authMe, isProductionUi, showManagerBlock]);

  const load = useCallback(() => {
    if (authMe) {
      const rc = authMe.reason_code;
      if (authMe.portal_access === 'no_license' || rc === 'license_disabled') {
        setState(STATE.FORBIDDEN);
        setData(null);
        return;
      }
      if (rc === 'membership_revoked') {
        setState(STATE.FORBIDDEN);
        setData(null);
        return;
      }
      if (authMe.portal_access === 'no_link' && authMe.identity_link_status !== 'linked') {
        setState(STATE.NOT_LINKED);
        setData(null);
        return;
      }
      if (authMe.portal_access === 'link_suspended' || rc === 'identity_link_suspended') {
        setState(STATE.LINK_SUSPENDED);
        setData(null);
        return;
      }
    }
    setState(STATE.LOADING);
    setSupportId(null);
    api
      .get('/api/employee-portal/summary')
      .then((r) => {
        setData(r.data);
        setState(STATE.OK);
      })
      .catch((err) => {
        setData(null);
        const st = classifyError(err);
        setState(st);
        const hdr = err?.response?.headers?.['x-request-id'] || err?.response?.headers?.['x-correlation-id'];
        if (hdr) setSupportId(String(hdr));
      });
  }, [authMe]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (state !== STATE.OK) return;
    let cancelled = false;
    setCommsLoading(true);
    setCommsError(false);
    api
      .get('/api/communications')
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data.filter((c) => c.status === 'published') : [];
        setCommsPreview(list.slice(0, 4));
        setCommsLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setCommsPreview([]);
          setCommsError(true);
          setCommsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  useEffect(() => {
    if (state !== STATE.OK) return;
    let cancelled = false;
    setHubCultureLoading(true);
    setHubCultureError(false);
    getHubSummary()
      .then((r) => {
        if (!cancelled) {
          setHubCulture(r.data);
          setHubCultureLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHubCulture(null);
          setHubCultureError(true);
          setHubCultureLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  useEffect(() => {
    if (state !== STATE.OK || !showManagerBlock) {
      setManagerStats(null);
      return;
    }
    if (!canWith(permSummary, 'employees', 'view', isProductionUi)) {
      setManagerStats(null);
      return;
    }
    const managerEmployeeId = authMe?.employee_id;
    let cancelled = false;
    setManagerLoading(true);
    setManagerError(false);
    Promise.allSettled([api.get('/api/employees'), api.get('/api/leaves'), api.get('/api/expense-reports')])
      .then(([empRes, leaveRes, expRes]) => {
        if (cancelled) return;
        const employees = empRes.status === 'fulfilled' && Array.isArray(empRes.value.data) ? empRes.value.data : [];
        const leaves = leaveRes.status === 'fulfilled' && Array.isArray(leaveRes.value.data) ? leaveRes.value.data : [];
        const expenses = expRes.status === 'fulfilled' && Array.isArray(expRes.value.data) ? expRes.value.data : [];
        const teamSize = managerEmployeeId
          ? employees.filter((e) => e.manager_id === managerEmployeeId).length
          : 0;
        const pendingLeaves = leaves.filter((l) => ['pending', 'submitted'].includes(l.status)).length;
        const pendingExpenses = expenses.filter((x) => ['submitted', 'manager_approved'].includes(x.status)).length;
        setManagerStats({ teamSize, pendingLeaves, pendingExpenses });
        setManagerLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setManagerStats(null);
          setManagerError(true);
          setManagerLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [state, showManagerBlock, permSummary, isProductionUi, authMe?.employee_id]);

  if (state === STATE.LOADING) {
    return <div className="empty-state empty-state--premium">{t('common.loading')}</div>;
  }

  if (state !== STATE.OK || !data) {
    const keyMap = {
      [STATE.UNAUTH]: 'portal.errorUnauth',
      [STATE.FORBIDDEN]: 'portal.errorForbidden',
      [STATE.LINK_SUSPENDED]: 'portal.errorLinkSuspended',
      [STATE.NOT_LINKED]: 'portal.errorNotLinked',
      [STATE.NOT_FOUND]: 'portal.errorNotFound',
      [STATE.SERVER]: 'portal.errorServer',
      [STATE.NETWORK]: 'portal.errorNetwork',
      [STATE.UNKNOWN]: 'portal.errorUnknown',
    };
    return (
      <div className="page-stack portal-page">
        <div className="empty-state empty-state--boxed empty-state--premium portal-error-state">
          <h2 className="panel__title">{t(keyMap[state] || 'portal.errorUnknown')}</h2>
          {supportId ? (
            <p className="panel__muted text-sm">
              {t('portal.supportId')}: <code>{supportId}</code>
            </p>
          ) : null}
          <button type="button" className="btn btn--secondary btn--sm" onClick={load}>
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const emp = data.employee;
  const name = `${emp.first_name} ${emp.last_name}`.trim();
  const roleLine = [emp.position, emp.area].filter(Boolean).join(' · ') || t('portal.heroRoleFallback');

  return (
    <EmployeeHubView
      t={t}
      data={data}
      emp={emp}
      name={name}
      roleLine={roleLine}
      empStatusVariant={empStatusVariant}
      managerStats={managerStats}
      managerLoading={managerLoading}
      managerError={managerError}
      showManagerBlock={showManagerBlock}
      persona={persona}
      commsPreview={commsPreview}
      commsLoading={commsLoading}
      commsError={commsError}
      hubCulture={hubCulture}
      hubCultureLoading={hubCultureLoading}
      hubCultureError={hubCultureError}
    />
  );
}
